<template>
    <loading-page-layout :loaded="loaded">
        <expired-offers-snackbar :is-offers-expired="isOffersExpired" @refresh="goToSearchPage" />
        <v-row dense>
            <v-col>
                <mobile-info-page-tabs v-if="$breakpoint.smAndDown" v-model="mobileTabs" />
                <v-container fluid>
                    <simple-tour-breadcrumbs
                        v-if="!$breakpoint.smAndDown"
                        :search-request="searchRequest"
                        :product="product"
                    />
                    <v-row>
                        <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 0)" md="8">
                            <h1 v-if="product.info" class="text-h6">
                                {{ product.info.name }}
                            </h1>
                            <supplier-name-label v-if="isTO1" :supplier-name="product.supplierName" class="mb-2" />
                            <component
                                :is="!$breakpoint.smAndDown ? 'product-gallery' : 'product-gallery-mobile'"
                                :images-data="uniqueImages"
                            />
                            <template v-if="!$breakpoint.smAndDown">
                                <simple-tour-grouped-offers
                                    :dates-for-filter="datesForFilter"
                                    :sorted-filtered-offers="sortedFilteredOffers"
                                    full-card
                                    class="mt-4"
                                    @images="images = [...images, ...$event]"
                                    @info="offersInfo = [...offersInfo, $event]"
                                    @on-date-filter-change="onDateFilterChange"
                                />
                                <template v-if="groupOffersBy === 'none'">
                                    <simple-tour-offer-card
                                        v-for="offer in sortedFilteredOffers"
                                        :key="offer.offerKey"
                                        :offer="offer"
                                        @images="images = [...images, ...$event]"
                                        @info="offersInfo = [...offersInfo, $event]"
                                    />
                                </template>
                            </template>
                            <SimpleTourDescription v-if="offersInfo?.length" :offer-info="offersInfo[0]" />
                            <view-offers-button @show-offers="mobileTabs = 1" />
                        </v-col>
                        <v-col v-show="!$breakpoint.smAndDown || ($breakpoint.smAndDown && mobileTabs === 1)" md="4">
                            <simple-tour-search-summary :search-request="searchRequest" show-dates />
                            <template v-if="$breakpoint.smAndDown">
                                <simple-tour-grouped-offers
                                    :dates-for-filter="datesForFilter"
                                    :sorted-filtered-offers="sortedFilteredOffers"
                                    full-card
                                    @images="images = [...images, ...$event]"
                                    @info="offersInfo = [...offersInfo, $event]"
                                    @on-date-filter-change="onDateFilterChange"
                                />
                                <template v-if="groupOffersBy === 'none'">
                                    <simple-tour-offer-card
                                        v-for="(offer, offerIndex) in sortedFilteredOffers"
                                        :key="offerIndex"
                                        :offer="offer"
                                        @images="images = [...images, ...$event]"
                                        @info="offersInfo = [...offersInfo, $event]"
                                    />
                                </template>
                            </template>
                            <div v-if="!$breakpoint.smAndDown">
                                <client-only>
                                    <activity-map :offers="[]" :city="city" class="disable-top" />
                                </client-only>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <conditions-modal>
            <template v-slot:info>
                <activity-conditions-modal-info />
            </template>
        </conditions-modal>
        <simple-tour-offer-info-modal />
    </loading-page-layout>
</template>

<script lang="ts">
    import {Component, mixins} from 'vue-facing-decorator'
    import {SearchPageMixin} from '@/components/mixins/SearchPageMixin.vue'
    import {InfoPageMixin} from '@/components/mixins/InfoPageMixin.vue'
    import {SearchPageAuthMixin} from '@/components/mixins/SearchPageAuthMixin.vue'
    import {SimpleTourMixin} from '@/mixins/SimpleTourMixin.vue'
    import {SimpleTourPageMixin} from '@/components/simpleTours/mixins/SimpleTourPageMixin.vue'
    import LoadingPageLayout from '@/components/LoadingPageLayout.vue'
    import MobileInfoPageTabs from '@/components/snippets/MobileInfoPageTabs.vue'
    import ConditionsModal from '~/components/modals/ConditionsModal.vue'
    import ActivityConditionsModalInfo from '@/components/activities/snippets/ActivityConditionsModalInfo.vue'
    import ActivityMap from '@/components/activities/search/ActivityMap.vue'
    import ProductGallery from '@/components/info/ProductGallery.vue'
    import ProductGalleryMobile from '@/components/info/ProductGalleryMobile.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import ViewOffersButton from '@/components/snippets/ViewOffersButton.vue'
    import ExpiredOffersSnackbar from '@/components/search/ExpiredOffersSnackbar.vue'
    import {useSimpleTourRuntimeData} from '~/composables/sipmle-tour'
    import {sortOffersInsideProduct} from '@/filters/activitiesSort'
    import SimpleTourBreadcrumbs from '~/components/simpleTours/SimpleTourBreadcrumbs.vue'
    import SimpleTourSearchSummary from '~/components/simpleTours/search/SimpleTourSearchSummary.vue'
    import SimpleTourGroupedOffers from '~/components/simpleTours/search/offers/SimpleTourGroupedOffers.vue'
    import SimpleTourOfferCard from '~/components/simpleTours/info/SimpleTourOfferCard.vue'
    import SimpleTourOfferInfoModal from '~/components/simpleTours/info/SimpleTourOfferInfoModal.vue'

    @Component({
        components: {
            SimpleTourOfferInfoModal,
            SimpleTourOfferCard,
            SimpleTourGroupedOffers,
            SimpleTourSearchSummary,
            SimpleTourBreadcrumbs,
            LoadingPageLayout,
            MobileInfoPageTabs,
            ConditionsModal,
            ActivityConditionsModalInfo,
            ExpiredOffersSnackbar,
            ViewOffersButton,
            SupplierNameLabel,
            ActivityMap,
            ProductGallery,
            ProductGalleryMobile,
        },
        async setup() {
            const route = useRoute()
            await useAsyncData(`simple-tour-${route.query.cityId}`, async () => {
                await useSimpleTourRuntimeData(route)
                return true
            })
        },
    })
    export class SimpleTourPage extends mixins(
        SimpleTourPageMixin,
        SimpleTourMixin,
        SearchPageMixin,
        InfoPageMixin,
        SearchPageAuthMixin
    ) {
        offersInfo = []
        dateFilter = null

        onDateFilterChange(date) {
            this.dateFilter = date
        }

        get datesForFilter() {
            return this.product.offers?.map(o => o.date) || []
        }

        get sortedFilteredOffers() {
            let offers = [...(this.product.offers || [])]
            sortOffersInsideProduct[this.sortKey]?.({offers})
            if (this.dateFilter) {
                offers = offers.filter(offer => offer.date === this.dateFilter)
            }
            return offers
        }

        get sortKey() {
            return this.productStore.sortFnName
        }

        get itineraryDays() {
            return this.offersInfo.find(({itineraryDays}) => itineraryDays)?.itineraryDays
        }

        get uniqueImages() {
            return this.images.filter((v, i, a) => a.findIndex(t => t.url === v.url) === i)
        }

        get city() {
            return this.productRuntimeStore.city
        }

        get groupOffersBy() {
            return this.productStore.groupOffersBy
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default SimpleTourPage
</script>
<style scoped lang="scss">
    .disable-top {
        position: relative;
        top: 0 !important;
    }
</style>
